import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import {
  Search, X, Bold, Italic, List, BarChart3, AlertCircle, Loader2, ChevronDown
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const TAGS = ['Value', 'Croissance', 'Dividende', 'Contrarian', 'Crypto', 'ETF'];

// ── Ticker Autocomplete ──
const TickerSearch = ({ value, onChange, error }) => {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const search = useCallback(async (q) => {
    if (!q || q.length < 1) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/market/search?q=${encodeURIComponent(q)}`);
      const data = await res.json();
      setResults((data.results || []).slice(0, 8));
      setOpen(true);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, []);

  const handleInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    onChange({ symbol: '', name: '' }); // reset selection on typing
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(v), 300);
  };

  const select = (r) => {
    setQuery(`$${r.symbol}`);
    onChange({ symbol: r.symbol, name: r.name });
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <label className="block text-xs text-cap-text-secondary mb-1.5">
        Ticker associé <span className="text-red-400">*</span>
      </label>
      <div className={`flex items-center bg-[#0d0f14] border rounded-lg px-3 h-10 transition-colors ${error ? 'border-red-400/50' : 'border-white/10 focus-within:border-[#00e5a0]/40'}`}>
        <Search className="w-4 h-4 text-cap-text-muted mr-2 flex-shrink-0" />
        <input
          data-testid="ticker-input"
          className="flex-1 bg-transparent text-white text-sm outline-none placeholder:text-cap-text-muted"
          placeholder="Rechercher un ticker... (ex: AAPL)"
          value={query}
          onChange={handleInput}
          onFocus={() => results.length > 0 && setOpen(true)}
        />
        {loading && <Loader2 className="w-4 h-4 text-cap-text-muted animate-spin" />}
        {query && !loading && (
          <button onClick={() => { setQuery(''); onChange({ symbol: '', name: '' }); setResults([]); }}
            className="text-cap-text-muted hover:text-white"><X className="w-3.5 h-3.5" /></button>
        )}
      </div>
      {error && <p className="text-red-400 text-[11px] mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{error}</p>}
      {open && results.length > 0 && (
        <div className="absolute z-50 top-full mt-1 w-full bg-[#1a1d27] border border-white/10 rounded-lg shadow-2xl max-h-60 overflow-y-auto">
          {results.map((r) => (
            <button key={r.symbol} onClick={() => select(r)}
              className="w-full text-left px-3 py-2.5 hover:bg-white/5 transition-colors flex items-center justify-between"
              data-testid={`ticker-result-${r.symbol}`}>
              <div>
                <span className="font-mono font-bold text-[#00e5a0] text-sm">${r.symbol}</span>
                <span className="text-cap-text-secondary text-xs ml-2">{r.name}</span>
              </div>
              <span className="text-cap-text-muted text-[10px]">{r.exchange}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Rich Text Editor (simple markdown) ──
const RichTextArea = ({ value, onChange, error }) => {
  const textareaRef = useRef(null);

  const insertFormat = (prefix, suffix) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = value.substring(start, end);
    const before = value.substring(0, start);
    const after = value.substring(end);
    const newText = `${before}${prefix}${selected || 'texte'}${suffix}${after}`;
    onChange(newText);
    setTimeout(() => {
      ta.focus();
      const cursorPos = start + prefix.length + (selected || 'texte').length;
      ta.setSelectionRange(start + prefix.length, cursorPos);
    }, 0);
  };

  return (
    <div>
      <label className="block text-xs text-cap-text-secondary mb-1.5">
        Corps de la thèse <span className="text-red-400">*</span>
        <span className="text-cap-text-muted ml-1">(min 200 caractères)</span>
      </label>
      {/* Toolbar */}
      <div className={`border rounded-t-lg bg-[#0d0f14] px-2 py-1.5 flex items-center gap-1 transition-colors ${error ? 'border-red-400/50' : 'border-white/10'}`}>
        <button type="button" onClick={() => insertFormat('**', '**')} title="Gras"
          className="p-1.5 rounded hover:bg-white/10 text-cap-text-secondary hover:text-white transition-colors" data-testid="format-bold">
          <Bold className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => insertFormat('*', '*')} title="Italique"
          className="p-1.5 rounded hover:bg-white/10 text-cap-text-secondary hover:text-white transition-colors" data-testid="format-italic">
          <Italic className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => insertFormat('\n- ', '')} title="Liste"
          className="p-1.5 rounded hover:bg-white/10 text-cap-text-secondary hover:text-white transition-colors" data-testid="format-list">
          <List className="w-4 h-4" />
        </button>
        <div className="flex-1" />
        <span className={`text-[10px] font-mono ${value.length >= 200 ? 'text-[#00e5a0]' : 'text-cap-text-muted'}`}>
          {value.length}/200 min
        </span>
      </div>
      <textarea
        ref={textareaRef}
        data-testid="thesis-body-input"
        className={`w-full bg-[#0d0f14] border border-t-0 rounded-b-lg px-3 py-3 text-white text-sm outline-none resize-none placeholder:text-cap-text-muted min-h-[180px] transition-colors ${error ? 'border-red-400/50' : 'border-white/10 focus:border-[#00e5a0]/40'}`}
        placeholder="Développez votre thèse d'investissement...&#10;&#10;Utilisez la barre d'outils pour formater : **gras**, *italique*, - listes"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {error && <p className="text-red-400 text-[11px] mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{error}</p>}
    </div>
  );
};

// ── Analysis Picker ──
const AnalysisPicker = ({ userId, value, onChange }) => {
  const [analyses, setAnalyses] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!userId) return;
    const token = localStorage.getItem('cap_token');
    if (!token) return;
    fetch(`${API}/api/analyses`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(list => {
        if (!Array.isArray(list)) return;
        setAnalyses(list.filter(a => a.status === 'completed'));
      })
      .catch(() => {});
  }, [userId]);

  if (analyses.length === 0) return null;

  const selected = analyses.find(a => a.id === value);

  return (
    <div>
      <label className="block text-xs text-cap-text-secondary mb-1.5">
        Lier une analyse CAP OS <span className="text-cap-text-muted">(optionnel)</span>
      </label>
      <div className="relative">
        <button type="button" onClick={() => setOpen(!open)}
          className="w-full bg-[#0d0f14] border border-white/10 rounded-lg px-3 h-10 flex items-center justify-between text-sm hover:border-white/20 transition-colors"
          data-testid="analysis-picker">
          {selected ? (
            <span className="flex items-center gap-2 text-white">
              <BarChart3 className="w-4 h-4 text-[#4f8eff]" />
              {selected.companyName || selected.ticker} — Score {selected.score}/100
            </span>
          ) : (
            <span className="text-cap-text-muted">Sélectionner une analyse...</span>
          )}
          <ChevronDown className={`w-4 h-4 text-cap-text-muted transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <div className="absolute z-50 top-full mt-1 w-full bg-[#1a1d27] border border-white/10 rounded-lg shadow-2xl max-h-48 overflow-y-auto">
            <button onClick={() => { onChange(null); setOpen(false); }}
              className="w-full text-left px-3 py-2.5 hover:bg-white/5 text-cap-text-muted text-xs">
              Aucune analyse
            </button>
            {analyses.map(a => (
              <button key={a.id} onClick={() => { onChange(a.id); setOpen(false); }}
                className={`w-full text-left px-3 py-2.5 hover:bg-white/5 transition-colors flex items-center gap-2 ${value === a.id ? 'bg-[#00e5a0]/5' : ''}`}>
                <BarChart3 className="w-4 h-4 text-[#4f8eff] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm truncate">{a.companyName || a.ticker}</p>
                  <p className="text-cap-text-muted text-[10px]">{a.ticker} — Score {a.score || '?'}/100 — {new Date(a.date).toLocaleDateString('fr-FR')}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════
// MAIN MODAL
// ══════════════════════════════════════
const ThesisFormModal = ({ open, onClose, onSubmit, userId, username }) => {
  const [title, setTitle] = useState('');
  const [ticker, setTicker] = useState({ symbol: '', name: '' });
  const [tags, setTags] = useState([]);
  const [body, setBody] = useState('');
  const [summary, setSummary] = useState('');
  const [analysisId, setAnalysisId] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Reset form when opening
  useEffect(() => {
    if (open) {
      setTitle(''); setTicker({ symbol: '', name: '' }); setTags([]); setBody('');
      setSummary(''); setAnalysisId(null); setErrors({}); setSubmitting(false);
    }
  }, [open]);

  const toggleTag = (tag) => {
    setTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
    setErrors(prev => ({ ...prev, tags: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!title.trim()) e.title = 'Le titre est obligatoire';
    else if (title.length > 100) e.title = 'Maximum 100 caractères';
    if (!ticker.symbol) e.ticker = 'Sélectionnez un ticker';
    if (tags.length === 0) e.tags = 'Sélectionnez au moins un tag';
    if (!body.trim()) e.body = 'Le corps de la thèse est obligatoire';
    else if (body.trim().length < 200) e.body = `Minimum 200 caractères (${body.trim().length}/200)`;
    if (summary.length > 200) e.summary = 'Maximum 200 caractères';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    const thesisData = {
      title: title.trim(),
      symbol: ticker.symbol,
      symbol_name: ticker.name,
      content: body.trim(),
      summary: summary.trim() || null,
      tags,
      analysis_id: analysisId || null,
      has_analysis: !!analysisId,
    };
    try {
      await onSubmit(thesisData);
    } catch {
      // Parent handles error toast
    } finally {
      setSubmitting(false);
    }
  };

  const isValid = title.trim() && ticker.symbol && tags.length > 0 && body.trim().length >= 200 && summary.length <= 200;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="bg-[#141720] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto p-0" data-testid="thesis-form-modal">
        <DialogHeader className="px-6 pt-6 pb-0">
          <DialogTitle className="font-display text-xl font-bold text-white">Publier une thèse</DialogTitle>
          <DialogDescription className="text-cap-text-secondary text-sm">
            Partagez votre conviction d'investissement avec la communauté
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 pb-6 space-y-5">
          {/* Title */}
          <div>
            <label className="block text-xs text-cap-text-secondary mb-1.5">
              Titre <span className="text-red-400">*</span>
              <span className="text-cap-text-muted ml-1">({title.length}/100)</span>
            </label>
            <input
              data-testid="thesis-title-input"
              className={`w-full bg-[#0d0f14] border rounded-lg px-3 h-10 text-white text-sm outline-none placeholder:text-cap-text-muted transition-colors ${errors.title ? 'border-red-400/50' : 'border-white/10 focus:border-[#00e5a0]/40'}`}
              placeholder="Ex: Apple : la forteresse du cash-flow"
              value={title}
              onChange={(e) => { setTitle(e.target.value.slice(0, 100)); setErrors(prev => ({ ...prev, title: undefined })); }}
              maxLength={100}
            />
            {errors.title && <p className="text-red-400 text-[11px] mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.title}</p>}
          </div>

          {/* Ticker */}
          <TickerSearch
            value={ticker.symbol ? `$${ticker.symbol}` : ''}
            onChange={(t) => { setTicker(t); setErrors(prev => ({ ...prev, ticker: undefined })); }}
            error={errors.ticker}
          />

          {/* Tags */}
          <div>
            <label className="block text-xs text-cap-text-secondary mb-1.5">
              Tags <span className="text-red-400">*</span>
              <span className="text-cap-text-muted ml-1">(min 1)</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {TAGS.map(tag => (
                <button key={tag} type="button" onClick={() => toggleTag(tag)}
                  data-testid={`tag-${tag.toLowerCase()}`}
                  className={`text-xs px-3 py-1.5 rounded-full transition-all font-medium ${tags.includes(tag) ? 'bg-[#00e5a0]/20 text-[#00e5a0] border border-[#00e5a0]/30' : 'bg-white/5 text-cap-text-secondary border border-transparent hover:bg-white/10'}`}>
                  #{tag}
                </button>
              ))}
            </div>
            {errors.tags && <p className="text-red-400 text-[11px] mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.tags}</p>}
          </div>

          {/* Body */}
          <RichTextArea value={body} onChange={(v) => { setBody(v); setErrors(prev => ({ ...prev, body: undefined })); }} error={errors.body} />

          {/* Analysis Picker */}
          <AnalysisPicker userId={userId} value={analysisId} onChange={setAnalysisId} />

          {/* Summary */}
          <div>
            <label className="block text-xs text-cap-text-secondary mb-1.5">
              Résumé court <span className="text-cap-text-muted">(optionnel, {summary.length}/200)</span>
            </label>
            <input
              data-testid="thesis-summary-input"
              className={`w-full bg-[#0d0f14] border rounded-lg px-3 h-10 text-white text-sm outline-none placeholder:text-cap-text-muted transition-colors ${errors.summary ? 'border-red-400/50' : 'border-white/10 focus:border-[#00e5a0]/40'}`}
              placeholder="Résumé affiché dans le feed..."
              value={summary}
              onChange={(e) => { setSummary(e.target.value.slice(0, 200)); setErrors(prev => ({ ...prev, summary: undefined })); }}
              maxLength={200}
            />
            {errors.summary && <p className="text-red-400 text-[11px] mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors.summary}</p>}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-2 border-t border-white/5">
            <p className="text-cap-text-muted text-[10px] flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full bg-[#00e5a0]" /> +30 XP à la publication
            </p>
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={onClose} className="text-cap-text-secondary text-xs h-9"
                data-testid="thesis-cancel-btn">
                Annuler
              </Button>
              <Button
                data-testid="thesis-submit-btn"
                onClick={handleSubmit}
                disabled={!isValid || submitting}
                className="bg-[#00e5a0] text-[#0d0f14] font-semibold text-xs h-9 px-5 disabled:opacity-40 disabled:cursor-not-allowed">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Publier
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ThesisFormModal;
